import React, {FC, lazy, RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {usePrevious} from '../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useInitialEffect} from '../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {DataEventTrackingProvider} from '../../libs/shared/core/src/services/tracking/dataEventTrackingProvider';
import {InterfaceEventsEnum} from '../../libs/shared/core/src/services/tracking/tracking';
import {AnalyticsPanelId} from '../../libs/shared/core/src/store/panels/panelStateBase';
import {Loader} from '../../libs/shared/ui/src/components/loaders/loader';
import {VisualSizesEnum} from '../../libs/shared/ui/src/styles/commonStyles';
import {LightUserThemeProvider} from '../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../helpers/appReduxHelpers';
import {wrapWithSuspense} from '../helpers/suspenseWrapperHelpers';
import {AnalyticsFilterTypesEnum} from '../models/api/analyticsSavedViewModel';
import {getCompanyHasAnalytics} from '../store/data/company/companySelectors';
import {
  navigateAnalyticsAction,
  setSelectedNamespaceAction,
} from '../store/panels/analytics/analyticsPanelActions';
import {AnalyticsPanelActionsEnum} from '../store/panels/analytics/analyticsPanelActionsTypes';
import {getIsAnalyticsLoaded} from '../store/panels/analytics/analyticsPanelSelectors';
import {getHasViewChanged} from '../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {
  getAnalyticsSelectedNamespace,
  makeGetAnalyticsParameters,
} from '../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {AnalyticsParameters} from '../store/panels/analytics/parameters/analyticsParameters';
import {
  fetchGetInboxesWithTicketingAsyncAction,
  fetchGetInboxIdsWithConversationStagesBundlesAsyncAction,
} from '../store/panels/analytics/report/analyticsPanelReportActions';
import {fetchSavedViewsAsyncAction} from '../store/panels/analytics/views/analyticsPanelSavedViewsActions';
import {
  DEFAULT_VIEW_ID,
  isAnalyticsViewSaved,
} from '../store/panels/analytics/views/analyticsPanelViewsState';
import {navigateToLinkAction} from '../store/stateless/navigationActions';
import {Chatbots} from './components/automationReport/chatbots/chatbots';
import {KnowledgeBaseReport} from './components/automationReport/knowledgeBase/knowledgeBaseReport';
import {SupportReportConfiguration} from './components/configuration/supportReportConfiguration';
import {ReportDiscardChanges} from './components/discardChanges/reportDiscardChanges';
import {AnalyticsFilterItem} from './components/filters/common/helpers';
import {useAnalyticsInboxesFilter} from './components/filters/useAnalyticsInboxesFilter';
import {useAnalyticsTeammatesFilter} from './components/filters/useAnalyticsTeammatesFilter';
import {useAnalyticsTeamsFilter} from './components/filters/useAnalyticsTeamsFilter';
import {LiveDashboardContainer} from './components/realTimeReport/liveDashboardContainer';
import {useExtraFilters} from './components/reportHeader/filtersRow/useExtraFilters';
import {ReportNavigation} from './components/reportNavigation/reportNavigation';
import {AnalyticsEmptyScreenUpsell} from './components/reportViewer/upsell/analyticsEmptyScreenUpsell';
import {buildAnalyticsPath} from './helpers/analyticsUrlHelpers';
import {AnalyticsReportsEnum, AnalyticsV3Reports} from './helpers/reportHelpers/reportTypes';
import {useAnalyticsTracking} from './helpers/tracking/useAnalyticsTracking';

/*
 * Props.
 */

interface AnalyticsAppProps {
  panelId: AnalyticsPanelId;
  isVisible: boolean;
}

export type AnalyticsFiltersList = {
  [AnalyticsFilterTypesEnum.TEAMS]: AnalyticsFilterItem;
  [AnalyticsFilterTypesEnum.INBOXES]: AnalyticsFilterItem;
  [AnalyticsFilterTypesEnum.TEAMMATES]: AnalyticsFilterItem;
  extraFilters: {
    activeExtraFilters: ReadonlyArray<AnalyticsFilterItem>;
    inactiveExtraFilters: ReadonlyArray<AnalyticsFilterItem>;
  };
};

/*
 * Styles.
 */

const StyledGridDiv = styled.div`
  display: grid;
  grid-template-columns: auto auto 1fr auto;
  min-height: 250px;
  height: 100%;
`;

const StyledLoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

/*
 * Component.
 */

/**
 * Right now only lazy loading OldReportViewer so P1 is always displayed and it doesn't flicker when arriving on the page.
 * Most of the code will be in OldReportViewer anyway so not critical for now.
 */
const OldReportViewer = lazy(() =>
  // @ts-expect-error Bypass the TS error around wrong module type since the `import()` directive is handled by Webpack
  import(
    /* webpackChunkName: 'analytics', webpackPrefetch: true */ './components/reportViewer/oldReportViewer'
  ).then((module) => ({
    default: module.OldReportViewer,
  })),
);

const ReportViewer = lazy(() =>
  // @ts-expect-error Bypass the TS error around wrong module type since the `import()` directive is handled by Webpack
  import(
    /* webpackChunkName: 'analytics-v3', webpackPrefetch: true */ './components/reportViewer/reportViewer'
  ).then((module) => ({
    default: module.ReportViewer,
  })),
);

export const AnalyticsApp: FC<AnalyticsAppProps> = ({panelId, isVisible}) => {
  const getPanelId = useMemo(() => () => panelId, [panelId]);
  const dispatch = useAppDispatch();
  const trackAnalytics = useAnalyticsTracking();
  const [isBetaBannerOpen, setIsBetaBannerOpen] = useState(true);

  const isLoaded = useAppSelector(getIsAnalyticsLoaded);

  const parameters = useAppSelector(makeGetAnalyticsParameters(getPanelId));
  const selectedNamespace = useAppSelector(getAnalyticsSelectedNamespace);
  const hasViewChanged = useAppSelector(getHasViewChanged);

  const hasAnalytics = useAppSelector(getCompanyHasAnalytics);

  const shouldDisplayEmptyScreenUpsell = !hasAnalytics;
  const previousSelectedNamespace = usePrevious(selectedNamespace);

  // Create filters here so they can be shared between the v2 and v3 reports.
  // TODO move back into ReportHeaderFilters once v2 is removed.
  const filtersList: AnalyticsFiltersList = {
    [AnalyticsFilterTypesEnum.TEAMS]: useAnalyticsTeamsFilter(),
    [AnalyticsFilterTypesEnum.INBOXES]: useAnalyticsInboxesFilter(),
    [AnalyticsFilterTypesEnum.TEAMMATES]: useAnalyticsTeammatesFilter(),
    extraFilters: useExtraFilters(),
  };

  const navigationCommunicationSectionRef = useRef<HTMLDivElement>(null);

  /*
   * Lifecycle.
   *
   * TODO: Rather than running this in the react layer, fetching the correct resources should ideally
   * all happen in the redux layer - potentially in the form of a better navigate analytics action
   * that handles the correct fetching for different scenarios. There are some circular dependencies to work
   * out if we do go down that path.
   */

  const fetchSavedViewsAndInitialData = useCallback(async () => {
    const {query, reportType: parametersReportType} = parameters;

    dispatch(setSelectedNamespaceAction(panelId, selectedNamespace));

    // The fetch saved view action calls the updateFiltersAction which would usually load the data,
    // but it checks if analytics is loaded before running the fetch. The status should always be Loading
    // here, so we won't ever run a double fetch here, even though it seems like we could.
    //
    // Also, we need the await here because until the views have been loaded, we don't know what filters are
    // applied/what data we need to load.
    //
    // TODO: refactor those functions to avoid the side effects/make it more clear?

    const selectedViewId = isAnalyticsViewSaved(query.selectedViewId)
      ? Number(query.selectedViewId)
      : undefined;

    await Promise.all([
      dispatch(fetchSavedViewsAsyncAction(selectedNamespace, true, selectedViewId)),
      dispatch(fetchGetInboxIdsWithConversationStagesBundlesAsyncAction()),
      dispatch(fetchGetInboxesWithTicketingAsyncAction(selectedNamespace)),
    ]);

    // We mark this as loaded before fetching all the metrics because at this point we can render
    // the saved views + the filters + the skeletons for the metrics, and the progress bar for the metric loading.
    dispatch({
      type: AnalyticsPanelActionsEnum.LOADED,
      payload: {reportType: parametersReportType},
    });

    await dispatch(navigateAnalyticsAction(panelId, parameters, true));
  }, [dispatch, panelId, parameters, selectedNamespace]);

  useInitialEffect(() => {
    fetchSavedViewsAndInitialData();
  });

  // Hook for loading data on namespace change.
  useEffect(() => {
    if (
      // Confirm that these values are both defined so that we don't
      // run this hook on the initial load.
      selectedNamespace &&
      previousSelectedNamespace &&
      selectedNamespace !== previousSelectedNamespace
    ) {
      fetchSavedViewsAndInitialData();
    }
  }, [fetchSavedViewsAndInitialData, previousSelectedNamespace, selectedNamespace]);

  /*
   * Event handlers.
   */

  const onSelectedNamespaceChange = (namespace: string) => {
    if (namespace !== selectedNamespace) {
      trackAnalytics(InterfaceEventsEnum.ANALYTICS_NAMESPACE_SELECTED, {
        namespace,
        previousNamespace: selectedNamespace,
        reportType: parameters.reportType,
      });

      // Keep the URL in sync.
      const path = buildAnalyticsPath({
        ...parameters,
        query: {...parameters.query, selectedViewId: DEFAULT_VIEW_ID},
        namespace,
        filters: undefined,
        options: undefined,
      });
      dispatch(navigateToLinkAction(path));
    }
  };

  /*
   * Component.
   */

  const maybeRenderAnalyticsApp = () => {
    if (shouldDisplayEmptyScreenUpsell) {
      return <AnalyticsEmptyScreenUpsell namespace={selectedNamespace} />;
    }

    if (!isLoaded) {
      return (
        <StyledLoaderWrapper>
          <Loader size={VisualSizesEnum.LARGE} />
        </StyledLoaderWrapper>
      );
    }

    return wrapWithSuspense(
      <>
        {maybeRenderReport(
          parameters,
          isVisible,
          filtersList,
          panelId,
          isBetaBannerOpen,
          setIsBetaBannerOpen,
          navigationCommunicationSectionRef,
        )}
        <ReportDiscardChanges panelId={panelId} hasViewChanged={hasViewChanged} />
      </>,
    );
  };

  return (
    <StyledGridDiv>
      <DataEventTrackingProvider context={{name: 'analytics', payload: {namespace: selectedNamespace}}}>
        <ReportNavigation
          panelId={panelId}
          selectedNamespace={selectedNamespace}
          onSelectedNamespaceChange={onSelectedNamespaceChange}
          isDisabled={shouldDisplayEmptyScreenUpsell}
          selectedReportType={parameters.reportType}
          communicationSectionRef={navigationCommunicationSectionRef}
        />
        <LightUserThemeProvider>{maybeRenderAnalyticsApp()}</LightUserThemeProvider>
      </DataEventTrackingProvider>
    </StyledGridDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderReport(
  parameters: AnalyticsParameters,
  isVisible: boolean,
  filtersList: AnalyticsFiltersList,
  panelId: AnalyticsPanelId,
  isBetaBannerOpen: boolean,
  setIsBetaBannerOpen: (value: boolean) => void,
  navigationCommunicationSectionRef: RefObject<HTMLDivElement>,
) {
  if (!parameters.reportType) {
    return null;
  }

  // Analytics V3 reports.
  if (AnalyticsV3Reports.includes(parameters.reportType)) {
    return (
      <ReportViewer
        isVisible={isVisible}
        panelId={panelId}
        filtersList={filtersList}
        navigationCommunicationSectionRef={navigationCommunicationSectionRef}
      />
    );
  }

  // Chatbot report.
  if (parameters.reportType === AnalyticsReportsEnum.CHATBOTS) {
    return <Chatbots parameters={parameters} panelId={panelId} />;
  }

  // KB report.
  if (parameters.reportType === AnalyticsReportsEnum.KNOWLEDGE_BASE) {
    return <KnowledgeBaseReport panelId={panelId} parameters={parameters} isVisible={isVisible} />;
  }

  // Support report configuration.
  if (parameters.reportType === AnalyticsReportsEnum.SUPPORT_REPORT) {
    return <SupportReportConfiguration panelId={panelId} parameters={parameters} isVisible={isVisible} />;
  }

  // Live dashboard report.
  if (parameters.reportType === AnalyticsReportsEnum.LIVE_DASHBOARD) {
    return (
      <LiveDashboardContainer
        panelId={panelId}
        parameters={parameters}
        isBetaBannerOpen={isBetaBannerOpen}
        setIsBetaBannerOpen={setIsBetaBannerOpen}
      />
    );
  }

  // Analytics default reports.
  return <OldReportViewer isVisible={isVisible} filtersList={filtersList} panelId={panelId} />;
}
